// @ts-nocheck
import React, { useEffect, useState, useCallback } from 'react';
import { useRecordContext, useNotify } from 'react-admin';
import {
	Card,
	CardContent,
	Typography,
	Button,
	Box,
	Stack,
	CircularProgress,
	Alert,
	Chip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	TableHead,
	Paper,
	Link,
	TextField,
	IconButton,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
	getChatStatus,
	getChatUsage,
	createChatInstance,
	destroyChatInstance,
	LlmInstance,
	UserUsage,
} from '../../Services/chat';

const SSH_BASTION = (window as any).SSH_BASTION || '';
const SSH_PN_SUFFIX = (window as any).SSH_PN_SUFFIX || '';

// #region StatusChip
const StatusChip = ({ status }: { status: string }) => {
	const colorMap: Record<string, 'success' | 'error' | 'warning' | 'info'> = {
		active: 'success',
		error: 'error',
		creating: 'warning',
		destroying: 'info',
	};

	return (
		<Chip
			label={status}
			color={colorMap[status] || 'default'}
			size="small"
			sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}
		/>
	);
};
// #endregion

// #region InstanceDetails
const InstanceDetails = ({ llm }: { llm: LlmInstance }) => {
	return (
		<TableContainer component={Paper} variant="outlined">
			<Table size="small">
				<TableBody>
					<TableRow>
						<TableCell sx={{ fontWeight: 'bold', width: 180 }}>Statut</TableCell>
						<TableCell>
							<StatusChip status={llm.status} />
							{llm.errorMessage && (
								<Typography variant="caption" color="error" sx={{ ml: 1 }}>
									{llm.errorMessage}
								</Typography>
							)}
						</TableCell>
					</TableRow>
					<TableRow>
						<TableCell sx={{ fontWeight: 'bold' }}>Domaine</TableCell>
						<TableCell>
							<Link
								href={`https://${llm.domain}`}
								target="_blank"
								rel="noopener"
								sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
							>
								{llm.domain}
								<OpenInNewIcon fontSize="small" />
							</Link>
						</TableCell>
					</TableRow>
					<TableRow>
						<TableCell sx={{ fontWeight: 'bold' }}>IP publique</TableCell>
						<TableCell>{llm.publicIp || '-'}</TableCell>
					</TableRow>
					<TableRow>
						<TableCell sx={{ fontWeight: 'bold' }}>Provider</TableCell>
						<TableCell>{llm.llmProvider}</TableCell>
					</TableRow>
					<TableRow>
						<TableCell sx={{ fontWeight: 'bold' }}>Modèle</TableCell>
						<TableCell>
							<Chip label={llm.llmModel} size="small" variant="outlined" />
						</TableCell>
					</TableRow>
					<TableRow>
						<TableCell sx={{ fontWeight: 'bold' }}>Région</TableCell>
						<TableCell>{llm.region}</TableCell>
					</TableRow>
					<TableRow>
						<TableCell sx={{ fontWeight: 'bold' }}>Instance ID</TableCell>
						<TableCell>
							<code style={{ fontSize: '0.85em' }}>{llm.instanceId || '-'}</code>
						</TableCell>
					</TableRow>
					{llm.slug && SSH_BASTION && SSH_PN_SUFFIX && (
						<TableRow>
							<TableCell sx={{ fontWeight: 'bold' }}>SSH</TableCell>
							<TableCell>
								{(() => {
									const cmd = `ssh -J ${SSH_BASTION} root@chat-${llm.slug}.${SSH_PN_SUFFIX}`;
									return (
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
											<code style={{ fontSize: '0.85em' }}>{cmd}</code>
											<Button
												size="small"
												sx={{ minWidth: 'auto', p: 0.5 }}
												onClick={() => {
													navigator.clipboard.writeText(cmd);
												}}
											>
												<ContentCopyIcon fontSize="small" />
											</Button>
										</Box>
									);
								})()}
							</TableCell>
						</TableRow>
					)}
					<TableRow>
						<TableCell sx={{ fontWeight: 'bold' }}>Créé le</TableCell>
						<TableCell>{new Date(llm.createdAt).toLocaleString('fr-FR')}</TableCell>
					</TableRow>
					{llm.adminPassword && (
						<>
							<TableRow>
								<TableCell sx={{ fontWeight: 'bold' }} colSpan={2}>
									<Typography variant="subtitle2" sx={{ mt: 1 }}>Admin Open WebUI</Typography>
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
								<TableCell><code style={{ fontSize: '0.85em' }}>admin@quanti.io</code></TableCell>
							</TableRow>
							<TableRow>
								<TableCell sx={{ fontWeight: 'bold' }}>Mot de passe</TableCell>
								<TableCell><code style={{ fontSize: '0.85em' }}>{llm.adminPassword}</code></TableCell>
							</TableRow>
						</>
					)}
				</TableBody>
			</Table>
		</TableContainer>
	);
};
// #endregion

// #region formatTokens
const formatTokens = (n: number): string => {
	if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
	if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
	return n.toString();
};

// #region UsageTable
const UsageTable = ({ projectId }: { projectId: number }) => {
	const now = new Date();
	const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
	const [usage, setUsage] = useState<UserUsage[]>([]);
	const [loading, setLoading] = useState(false);

	const fetchUsage = useCallback(async () => {
		setLoading(true);
		try {
			const res = await getChatUsage(projectId, month);
			setUsage(res.usage || []);
		} catch {
			setUsage([]);
		} finally {
			setLoading(false);
		}
	}, [projectId, month]);

	useEffect(() => {
		fetchUsage();
	}, [fetchUsage]);

	const shiftMonth = (delta: number) => {
		const [y, m] = month.split('-').map(Number);
		const d = new Date(y, m - 1 + delta, 1);
		setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
	};

	const totals = usage.reduce(
		(acc, u) => ({
			promptTokens: acc.promptTokens + u.promptTokens,
			completionTokens: acc.completionTokens + u.completionTokens,
			totalTokens: acc.totalTokens + u.totalTokens,
			requestCount: acc.requestCount + u.requestCount,
		}),
		{ promptTokens: 0, completionTokens: 0, totalTokens: 0, requestCount: 0 }
	);

	return (
		<Card>
			<CardContent>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
					<Typography variant="h6">Consommation LLM</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
						<IconButton size="small" onClick={() => shiftMonth(-1)}>
							<ChevronLeftIcon />
						</IconButton>
						<TextField
							size="small"
							type="month"
							value={month}
							onChange={(e) => setMonth(e.target.value)}
							sx={{ width: 170 }}
						/>
						<IconButton size="small" onClick={() => shiftMonth(1)}>
							<ChevronRightIcon />
						</IconButton>
					</Box>
				</Box>

				{loading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
						<CircularProgress size={24} />
					</Box>
				) : usage.length === 0 ? (
					<Alert severity="info">Aucune consommation sur cette période.</Alert>
				) : (
					<TableContainer component={Paper} variant="outlined">
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 'bold' }}>Utilisateur</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }} align="right">Requêtes</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }} align="right">Prompt</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }} align="right">Completion</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }} align="right">Total tokens</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{usage.map((u) => (
									<TableRow key={u.userEmail}>
										<TableCell>{u.userEmail}</TableCell>
										<TableCell align="right">{u.requestCount}</TableCell>
										<TableCell align="right">{formatTokens(u.promptTokens)}</TableCell>
										<TableCell align="right">{formatTokens(u.completionTokens)}</TableCell>
										<TableCell align="right">
											<strong>{formatTokens(u.totalTokens)}</strong>
										</TableCell>
									</TableRow>
								))}
								<TableRow sx={{ '& td': { fontWeight: 'bold', borderTop: 2 } }}>
									<TableCell>Total</TableCell>
									<TableCell align="right">{totals.requestCount}</TableCell>
									<TableCell align="right">{formatTokens(totals.promptTokens)}</TableCell>
									<TableCell align="right">{formatTokens(totals.completionTokens)}</TableCell>
									<TableCell align="right">{formatTokens(totals.totalTokens)}</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</CardContent>
		</Card>
	);
};
// #endregion

// #region ProjectChat
export const ProjectChat = () => {
	const record = useRecordContext();
	const notify = useNotify();

	const [llm, setLlm] = useState<LlmInstance | null>(null);
	const [loading, setLoading] = useState(true);
	const [actionLoading, setActionLoading] = useState(false);
	const [notFound, setNotFound] = useState(false);
	const [confirmDestroy, setConfirmDestroy] = useState(false);

	const projectId = record?.id;

	const fetchStatus = useCallback(async (silent = false) => {
		if (!projectId) return;
		if (!silent) {
			setLoading(true);
			setNotFound(false);
		}

		try {
			const res = await getChatStatus(projectId);
			setLlm(res.llm);
		} catch (error: any) {
			if (error?.code === 404 || error?.status === 404) {
				setNotFound(true);
				setLlm(null);
			} else {
				setNotFound(true);
				setLlm(null);
			}
		} finally {
			if (!silent) setLoading(false);
		}
	}, [projectId]);

	useEffect(() => {
		fetchStatus();
	}, [fetchStatus]);

	// Auto-refresh tant que le statut est "creating"
	useEffect(() => {
		if (!llm || llm.status !== 'creating') return;
		const interval = setInterval(() => fetchStatus(true), 10_000);
		return () => clearInterval(interval);
	}, [llm?.status, fetchStatus]);

	const handleCreate = async () => {
		setActionLoading(true);
		try {
			const res = await createChatInstance(projectId);
			setLlm(res.llm);
			setNotFound(false);
			notify('Instance Chat créée avec succès', { type: 'success' });
		} catch (error: any) {
			notify(error?.message || error?.error || 'Erreur lors de la création', {
				type: 'error',
			});
		} finally {
			setActionLoading(false);
		}
	};

	const handleDestroy = async () => {
		setConfirmDestroy(false);
		setActionLoading(true);
		try {
			await destroyChatInstance(projectId);
			setLlm(null);
			setNotFound(true);
			notify('Instance Chat supprimée', { type: 'success' });
		} catch (error: any) {
			notify(error?.message || error?.error || 'Erreur lors de la suppression', {
				type: 'error',
			});
		} finally {
			setActionLoading(false);
		}
	};

	if (!record) return null;

	return (
		<Stack spacing={2}>
			<Card>
				<CardContent>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
						<Typography variant="h6">
							Chat (Open WebUI)
						</Typography>
						<Box sx={{ display: 'flex', gap: 1 }}>
							<Button
								size="small"
								startIcon={<RefreshIcon />}
								onClick={fetchStatus}
								disabled={loading}
							>
								Rafraîchir
							</Button>
						</Box>
					</Box>

					{loading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
							<CircularProgress />
						</Box>
					) : notFound ? (
						<Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
							<Alert severity="info" sx={{ width: '100%' }}>
								Aucune instance Chat n'est configurée pour ce projet.
							</Alert>
							<Button
								variant="contained"
								color="primary"
								size="large"
								startIcon={actionLoading ? <CircularProgress size={20} /> : <PlayArrowIcon />}
								onClick={handleCreate}
								disabled={actionLoading}
							>
								{actionLoading ? 'Création en cours...' : 'Créer une instance Chat'}
							</Button>
						</Stack>
					) : llm ? (
						<Stack spacing={2}>
							<InstanceDetails llm={llm} />

							<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
								<Button
									variant="outlined"
									color="error"
									size="small"
									startIcon={actionLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
									onClick={() => setConfirmDestroy(true)}
									disabled={actionLoading}
								>
									Supprimer l'instance
								</Button>
							</Box>
						</Stack>
					) : null}
				</CardContent>
			</Card>

			{llm && <UsageTable projectId={projectId} />}

			{/* Dialog de confirmation suppression */}
			<Dialog open={confirmDestroy} onClose={() => setConfirmDestroy(false)}>
				<DialogTitle>Supprimer l'instance Chat ?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Cette action va supprimer l'instance Scaleway, retirer le DNS du load balancer
						et supprimer l'OAuthClient associé. Cette action est irréversible.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmDestroy(false)}>Annuler</Button>
					<Button onClick={handleDestroy} color="error" variant="contained">
						Supprimer
					</Button>
				</DialogActions>
			</Dialog>
		</Stack>
	);
};
// #endregion

export default ProjectChat;
